import React, { useRef, useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import vegaEmbed from 'vega-embed';
import { useDashboard } from '../hooks/useDashboard';
import { useChat } from '../hooks/useChat';
import MyChartsSection from './MyChartsSection';
import DashboardSettings from './DashboardSettings';
import ExportButton from './ExportButton';
import {
  MessageSquare, Users, Zap, Activity, Radio, Database,
  AlertTriangle, CheckCircle, XCircle, ChevronUp, ChevronDown, MousePointerClick, Filter, Check, Settings,
} from 'lucide-react';

// ── Layout ────────────────────────────────────────────────────────────────────

const Container = styled.div`
  /* The parent TabPanel is a flex column with overflow:hidden, so the dashboard owns its
     own vertical scroll. min-height:0 lets a flex child actually scroll. */
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 24px 24px 48px;
  /* Flex column lets us push the data-challenge panels below the operational charts
     (via order) without restructuring the JSX. */
  display: flex;
  flex-direction: column;
`;

const PageHeader = styled.div`
  margin-bottom: 28px;
  display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; flex-wrap: wrap;
`;
const HeaderControls = styled.div`
  display: flex; align-items: center; gap: 10px;
`;
const IconButton = styled.button`
  display: flex; align-items: center; justify-content: center; width: 40px; height: 40px;
  border-radius: 11px; border: 1px solid #e2e8f0; background: #fff; color: #475569; cursor: pointer;
  transition: all .15s;
  &:hover { border-color: rgba(27,140,60,0.45); color: #006200; }
`;

const PageTitle = styled.h1`
  font-size: 22px;
  font-weight: 700;
  color: #1a202c;
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 0 4px 0;
`;

const PageSubtitle = styled.p`
  font-size: 13px;
  color: #718096;
  margin: 0;
`;

// ── KPI Cards (matching Dashboard.js exactly) ─────────────────────────────────

const KpiCardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`;

const KpiCard = styled(motion.div)`
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  border: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  gap: 16px;
  transition: all 0.2s ease;
  &:hover {
    box-shadow: 0 4px 12px rgba(0,0,0,0.10);
    transform: translateY(-2px);
  }
`;

const KpiIconWrapper = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${p => p.$gradient || 'linear-gradient(135deg,#1B8C3C 0%,#006200 100%)'};
  color: white;
  flex-shrink: 0;
  svg { width: 24px; height: 24px; }
`;

const KpiContent = styled.div`flex: 1; min-width: 0;`;
const KpiValue = styled.div`
  font-size: 24px; font-weight: 700; color: #1a202c; line-height: 1.2;
`;
const KpiLabel = styled.div`font-size: 13px; color: #718096; margin-top: 4px;`;

// ── Grain Problem Panel ───────────────────────────────────────────────────────

const ProblemPanel = styled.div`
  order: 2;  /* render below the operational charts (data-challenge narrative, secondary) */
  background: white;
  border-radius: 16px;
  border: 2px solid ${p => p.$color || '#e53e3e'};
  padding: 20px 24px;
  margin-bottom: 20px;
  box-shadow: 0 4px 12px ${p => p.$shadow || 'rgba(229,62,62,0.10)'};
`;

const PanelTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 15px;
  font-weight: 700;
  color: ${p => p.$color || '#c53030'};
  margin-bottom: 4px;
  cursor: pointer;
  user-select: none;
  button {
    margin-left: auto;
    background: none;
    border: none;
    cursor: pointer;
    color: inherit;
    display: flex;
    align-items: center;
  }
`;

const PanelSubtitle = styled.p`
  font-size: 12px;
  color: #718096;
  margin: 0 0 16px 0;
`;

const GrainCols = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 16px;
  align-items: center;
`;

const GrainBox = styled.div`
  background: ${p => p.$correct ? '#f0fff4' : '#fff5f5'};
  border: 1px solid ${p => p.$correct ? '#9ae6b4' : '#fed7d7'};
  border-radius: 12px;
  padding: 16px 20px;
  text-align: center;
`;

const GrainTag = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${p => p.$correct ? '#276749' : '#9b2c2c'};
  background: ${p => p.$correct ? '#c6f6d5' : '#fed7d7'};
  padding: 3px 10px;
  border-radius: 20px;
  margin-bottom: 10px;
`;

const GrainNumber = styled.div`
  font-size: 36px;
  font-weight: 800;
  color: ${p => p.$correct ? '#276749' : '#e53e3e'};
  line-height: 1;
  margin-bottom: 8px;
`;

const GrainDesc = styled.div`
  font-size: 11px;
  color: #718096;
  font-family: monospace;
  white-space: pre-line;
  line-height: 1.5;
`;

const GrainDivider = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
`;

const FactorBadge = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg,#e53e3e 0%,#9b2c2c 100%);
  color: white;
  font-size: 18px;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(229,62,62,0.4);
`;

const FactorLabel = styled.div`
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #e53e3e;
  text-align: center;
`;

// ── Chart Section (matching Dashboard.js ChartSectionHeader + ChartCard) ─────

const SectionWrapper = styled.div`margin-bottom: 28px;`;

const ChartSectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  padding: 12px 16px;
  border-radius: 12px;
  background: white;
  border: 1px solid #e2e8f0;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
  .section-icon {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${p => p.$gradient || 'linear-gradient(135deg,#1B8C3C 0%,#006200 100%)'};
    color: white;
    svg { width: 22px; height: 22px; }
  }
  .section-info h3 { font-size: 18px; font-weight: 600; color: #2d3748; margin: 0 0 2px 0; }
  .section-info p { font-size: 13px; color: #718096; margin: 0; }
`;

const ChartsGrid = styled.div`
  display: grid;
  /* auto-fit + max(50%) → max 2 columns, drops to 1 if card would be < 380px */
  grid-template-columns: repeat(auto-fit, minmax(max(380px, calc(50% - 8px)), 1fr));
  gap: 16px;
`;

const ChartCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 6px rgba(0,0,0,0.05);
  border: 1px solid rgba(0,0,0,0.05);
  min-width: 0;
  .chart-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
  }
  .chart-title {
    font-size: 15px;
    font-weight: 600;
    color: #2d3748;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .chart-container {
    width: 100%;
    min-width: 200px;
    min-height: ${p => p.$height || 280}px;
    contain: layout;
  }
`;

const MvBadge = styled.span`
  font-size: 10px;
  font-weight: 600;
  color: #1B8C3C;
  background: #e6fffa;
  padding: 2px 8px;
  border-radius: 6px;
  font-family: monospace;
`;

const LoadingBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: #a0aec0;
  font-size: 14px;
`;

// ── VegaChart ─────────────────────────────────────────────────────────────────
// width:container + global labelLimit for readable axis labels.
// Cards must be wide enough (handled by the grid: minmax(380px, 1fr)).

function VegaChart({ chartData, height = 280, onPick }) {
  const containerRef = useRef(null);
  const lastWidthRef = useRef(0);
  const onPickRef = useRef(onPick);
  onPickRef.current = onPick;

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !chartData?.data?.length) return;

    const renderChart = () => {
      const baseMark = chartData.mark || { type: 'bar' };
      const mark = typeof baseMark === 'string'
        ? { type: baseMark, cursor: 'pointer' }
        : { ...baseMark, cursor: 'pointer' };
      // Colour each category from the palette (elegant + clear), unless the spec already encodes colour.
      const PALETTE = ['#1B8C3C', '#0ea5e9', '#6366f1', '#f59e0b', '#ef4444', '#8b5cf6', '#10b981', '#ec4899'];
      const baseEnc = chartData.encoding || {};
      let enc = baseEnc;
      if (!baseEnc.color) {
        let catField = null;
        for (const ch of ['x', 'y']) {
          const e = baseEnc[ch];
          if (e && (e.type === 'nominal' || e.type === 'ordinal') && e.field) { catField = e.field; break; }
        }
        if (catField) enc = { ...baseEnc, color: { field: catField, type: 'nominal', scale: { range: PALETTE }, legend: null } };
      }
      // Hover affordance: the bar under the cursor stays full while the others dim, so it visibly pops —
      // a clear "this is clickable / drill me" cue (cursor:pointer alone wasn't obvious enough).
      if (!enc.opacity) {
        enc = { ...enc, opacity: { condition: { param: 'barHover', value: 1 }, value: 0.4 } };
      }
      const spec = {
        $schema: 'https://vega.github.io/schema/vega-lite/v5.json',
        data:     { values: chartData.data },
        mark,
        encoding: enc,
        params:   [{ name: 'barHover', select: { type: 'point', on: 'pointerover', clear: 'pointerout' } }],
        width:    'container',
        height:   height,
        config: {
          font: 'Inter, "Noto Sans Arabic", sans-serif',   // match the app's UI font (Vega defaults to plain sans-serif)
          background: 'transparent',
          view: { stroke: null },
          bar: { cornerRadiusEnd: 5 },
          axis: {
            grid: false,
            labelFontSize: 11,
            titleFontSize: 12,
            labelFontWeight: 500,
            titleFontWeight: 600,
            labelLimit: 120,
            titlePadding: 8,
            labelColor: '#475569',
            titleColor: '#64748b',
            domainColor: '#e2e8f0',
            tickColor: '#e2e8f0',
          },
          legend: { labelFontSize: 12, titleFontSize: 12, labelColor: '#475569' },
          range: { category: ['#1B8C3C', '#0ea5e9', '#6366f1', '#f59e0b', '#ef4444', '#8b5cf6', '#10b981', '#ec4899'] },
        },
      };
      vegaEmbed(container, spec, { actions: false })
        .then(result => {
          // Click a bar/slice → drill into that segment as a chat conversation.
          if (result?.view && typeof result.view.addEventListener === 'function') {
            result.view.addEventListener('click', (event, item) => {
              if (item && item.datum && onPickRef.current) onPickRef.current(item.datum, event);
            });
          }
        })
        .catch(console.error);
    };

    const tryRender = () => {
      const rect = container.getBoundingClientRect();
      if (rect.width > 0) {
        renderChart();
        lastWidthRef.current = rect.width;
      }
    };

    tryRender();

    const observer = new ResizeObserver(entries => {
      for (const entry of entries) {
        const w = entry.contentRect.width;
        if (w > 0 && lastWidthRef.current === 0) tryRender();
        lastWidthRef.current = w;
      }
    });
    observer.observe(container);

    return () => observer.disconnect();
  }, [chartData, height]);

  if (!chartData?.data?.length) return <LoadingBox>No data</LoadingBox>;
  return <div ref={containerRef} style={{ width: '100%', minHeight: height }} />;
}

// ── Section component ─────────────────────────────────────────────────────────

function Section({ icon: Icon, title, subtitle, gradient, cols, children }) {
  return (
    <SectionWrapper>
      <ChartSectionHeader $gradient={gradient}>
        <div className="section-icon"><Icon /></div>
        <div className="section-info">
          <h3>{title}</h3>
          <p>{subtitle}</p>
        </div>
      </ChartSectionHeader>
      <ChartsGrid $cols={cols || 2}>{children}</ChartsGrid>
    </SectionWrapper>
  );
}

// ── ChartPanel component ──────────────────────────────────────────────────────

const DrillHint = styled.div`
  display: flex; align-items: center; gap: 4px;
  font-size: 11px; font-weight: 600; color: #1B8C3C; opacity: 0.85; white-space: nowrap;
`;

// ── Dashboard drill menu (clicking a bar opens a CHOOSER, not a sudden question) ──────────────
// Drill labels/topics/dims are NOT hardcoded here — they come from the pack (dashboards.json →
// `drill`), threaded in as `cfg`. Falls back gracefully when the pack ships none.
const topicFor = (cfg, mv) => (cfg && cfg.topics && cfg.topics[mv]) || 'البيانات';
const categoryAr = (cfg, label) => (cfg && cfg.category_labels && cfg.category_labels[label]) || label;
const drillQuestionFor = (cfg, mv, label, dim) => `${topicFor(cfg, mv)} في ${categoryAr(cfg, label)} ${dim}`;

const DrillBackdrop = styled.div`position: fixed; inset: 0; z-index: 9998;`;
const DrillMenuCard = styled(motion.div)`
  position: fixed; z-index: 9999; width: 280px;
  background: #fff; border: 1px solid #e2e8f0; border-radius: 14px;
  box-shadow: 0 14px 40px rgba(0,0,0,0.18); padding: 14px 16px;
  direction: rtl; text-align: right;
  .dm-title { font-size: 13.5px; font-weight: 800; color: #0f172a; margin-bottom: 3px; }
  .dm-sub { font-size: 11.5px; color: #94a3b8; margin-bottom: 11px; }
`;
const DrillOpt = styled.button`
  display: block; width: 100%; text-align: right; padding: 9px 12px; margin-bottom: 6px;
  border-radius: 9px; border: 1px solid rgba(27,140,60,0.25); background: rgba(27,140,60,0.06);
  color: #006200; font-size: 13px; font-weight: 600; cursor: pointer; transition: background 0.15s;
  &:last-child { margin-bottom: 0; }
  &:hover { background: rgba(27,140,60,0.13); }
`;

function DashboardDrillMenu({ drill, cfg, onPick, onClose }) {
  if (!drill) return null;
  const { x, y, label, sourceMv } = drill;
  const dims = (cfg && cfg.dims && cfg.dims.length) ? cfg.dims : ['عبر الزمن'];
  const left = Math.min(Math.max(x - 140, 12), window.innerWidth - 292);
  const top = Math.min(y + 8, window.innerHeight - 250);
  return (
    <>
      <DrillBackdrop onClick={onClose} />
      <DrillMenuCard style={{ left, top }} initial={{ opacity: 0, scale: 0.96, y: -6 }} animate={{ opacity: 1, scale: 1, y: 0 }}>
        <div className="dm-title">التعمّق في: {categoryAr(cfg, label)}</div>
        <div className="dm-sub">اختر زاوية — ستُفتح في محادثة جديدة</div>
        {dims.map((dim, i) => (
          <DrillOpt key={i} onClick={() => onPick(drillQuestionFor(cfg, sourceMv, label, dim))}>{dim}</DrillOpt>
        ))}
      </DrillMenuCard>
    </>
  );
}

// ── Local (per-chart) filter ──────────────────────────────────────────────────────────────────
const FilterWrap = styled.div`position: relative;`;
const FilterBtn = styled.button`
  display: flex; align-items: center; gap: 5px; padding: 5px 11px; border-radius: 999px;
  border: 1px solid ${p => (p.$active ? 'rgba(27,140,60,0.5)' : '#e2e8f0')};
  background: ${p => (p.$active ? 'rgba(27,140,60,0.1)' : '#fff')};
  color: ${p => (p.$active ? '#006200' : '#64748b')};
  font-size: 11.5px; font-weight: 700; cursor: pointer; white-space: nowrap;
  &:hover { border-color: rgba(27,140,60,0.45); }
  svg { width: 12px; height: 12px; }
`;
// Fixed-positioned (anchored to the button via getBoundingClientRect) so it is NEVER clipped by an
// ancestor's overflow — the absolute version was cut off by the chart card / scroll column.
const FilterPop = styled.div`
  position: fixed; z-index: 9999;
  background: #fff; border: 1px solid #e2e8f0; border-radius: 12px; box-shadow: 0 12px 32px rgba(15,23,42,0.14);
  padding: 8px; width: 248px; max-height: 320px; overflow-y: auto; direction: rtl; text-align: right;
`;
// Clear selected/unselected state (teal check + tint when ON, greyed when OFF) — matches the
// global region chips, so it's obvious what is included in the chart.
const FilterRow = styled.div`
  display: flex; align-items: center; gap: 9px; padding: 7px 9px; border-radius: 8px; cursor: pointer;
  font-size: 12.5px; user-select: none;
  font-weight: ${p => (p.$on ? 700 : 500)};
  color: ${p => (p.$on ? '#006200' : '#94a3b8')};
  background: ${p => (p.$on ? 'rgba(27,140,60,0.08)' : 'transparent')};
  &:hover { background: ${p => (p.$on ? 'rgba(27,140,60,0.14)' : '#f1f5f9')}; }
  .box { width: 18px; height: 18px; border-radius: 6px; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center; transition: all .12s;
    border: 1.5px solid ${p => (p.$on ? '#1B8C3C' : '#cbd5e1')};
    background: ${p => (p.$on ? '#1B8C3C' : '#fff')}; color: #fff; }
  .box svg { width: 12px; height: 12px; opacity: ${p => (p.$on ? 1 : 0)}; }
`;
const FilterClear = styled.button`
  width: 100%; margin-top: 4px; padding: 6px; border-radius: 8px; border: none; cursor: pointer;
  background: #f1f5f9; color: #475569; font-size: 12px; font-weight: 700;
  &:hover { background: #e2e8f0; }
`;

function ChartPanel({ chartData, mv, height, onOpenDrill, globalRegions, cfg }) {
  const [filterOpen, setFilterOpen] = useState(false);
  const [hidden, setHidden] = useState(() => new Set());
  const btnRef = React.useRef(null);
  const [popPos, setPopPos] = useState(null);

  // Anchor the fixed dropdown to the button; clamp into the viewport and flip up if low on screen.
  const openFilter = () => {
    if (filterOpen) { setFilterOpen(false); return; }
    const r = btnRef.current?.getBoundingClientRect();
    if (r) {
      const W = 248, MAXH = 320;
      let left = r.right - W;                         // RTL: align right edges
      if (left < 8) left = 8;
      if (left + W > window.innerWidth - 8) left = window.innerWidth - 8 - W;
      let top = r.bottom + 6;
      if (top + MAXH > window.innerHeight - 8) top = Math.max(8, r.top - 6 - MAXH);
      setPopPos({ top, left });
    }
    setFilterOpen(true);
  };

  // The filterable/drillable axis is the CATEGORY (nominal/ordinal), NOT the numeric value axis —
  // otherwise the filter lists the counts (e.g. 8598, 271…) instead of the category labels.
  const enc = chartData?.encoding || {};
  const catField = (() => {
    for (const ch of ['x', 'y', 'theta', 'color']) {
      const e = enc[ch];
      if (e && (e.type === 'nominal' || e.type === 'ordinal') && e.field) return e.field;
    }
    return enc.x?.field || enc.y?.field;
  })();
  const rows = chartData?.data || [];
  const categories = React.useMemo(
    () => [...new Set(rows.map(r => r?.[catField]).filter(v => v != null))],
    [rows, catField]
  );
  const catLabels = (cfg && cfg.category_labels) || {};
  const isRegionChart = catField === 'region' || categories.some(c => catLabels[c]);

  // Apply the GLOBAL region multi-select (only to region charts) + the LOCAL hidden-category filter.
  const filteredData = React.useMemo(() => {
    let d = rows;
    if (globalRegions && globalRegions.size > 0 && isRegionChart) {
      d = d.filter(r => globalRegions.has(r?.[catField]));
    }
    if (hidden.size > 0) d = d.filter(r => !hidden.has(r?.[catField]));
    return d;
  }, [rows, catField, hidden, globalRegions, isRegionChart]);

  const filteredChartData = React.useMemo(() => ({ ...chartData, data: filteredData }), [chartData, filteredData]);
  const localActive = hidden.size > 0;

  const toggle = (c) => setHidden(prev => {
    const n = new Set(prev);
    n.has(c) ? n.delete(c) : n.add(c);
    return n;
  });

  const handlePick = (datum, event) => {
    if (!onOpenDrill) return;
    const label = (catField && datum?.[catField] != null)
      ? datum[catField]
      : Object.values(datum || {}).find(v => typeof v === 'string' && v.trim());
    if (label == null) return;
    onOpenDrill({ x: event?.clientX ?? window.innerWidth / 2, y: event?.clientY ?? 200, label, sourceMv: mv });
  };

  return (
    <ChartCard $height={height || 280}>
      <div className="chart-header">
        <div className="chart-title">
          {chartData?.title}
          {mv && <MvBadge>{mv}</MvBadge>}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {onOpenDrill && <DrillHint><MousePointerClick size={12} /> انقر عموداً للتعمّق</DrillHint>}
          {categories.length > 1 && (
            <FilterWrap>
              <FilterBtn ref={btnRef} $active={localActive} onClick={openFilter}>
                <Filter /> فلترة
              </FilterBtn>
              {filterOpen && (
                <>
                  <div style={{ position: 'fixed', inset: 0, zIndex: 9998 }} onClick={() => setFilterOpen(false)} />
                  <FilterPop $rtl style={{ top: popPos?.top, left: popPos?.left }}>
                    {categories.map((c, i) => (
                      <FilterRow key={i} $on={!hidden.has(c)} onClick={() => toggle(c)}>
                        <span className="box"><Check /></span>
                        {categoryAr(cfg, c)}
                      </FilterRow>
                    ))}
                    {localActive && <FilterClear onClick={() => setHidden(new Set())}>إظهار الكل</FilterClear>}
                  </FilterPop>
                </>
              )}
            </FilterWrap>
          )}
        </div>
      </div>
      <div className="chart-container">
        <VegaChart chartData={filteredChartData} height={height || 260} onPick={onOpenDrill ? handlePick : undefined} />
      </div>
    </ChartCard>
  );
}

// ── Global filter bar ───────────────────────────────────────────────────────────────────────
const GlobalBar = styled.div`
  display: flex; align-items: center; gap: 12px; flex-wrap: wrap;
  background: #fff; border: 1px solid #eef2f7; border-radius: 14px; padding: 12px 16px;
  margin-bottom: 22px; box-shadow: 0 1px 3px rgba(0,0,0,0.04); direction: rtl;
  .gb-toggle { display: flex; align-items: center; gap: 8px; background: none; border: none; cursor: pointer;
    font-size: 13px; font-weight: 800; color: #006200; padding: 0; }
  .gb-count { background: #1B8C3C; color: #fff; font-size: 10.5px; font-weight: 800; border-radius: 999px; padding: 1px 7px; }
  .gb-chips { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
  svg { width: 15px; height: 15px; }
`;
const RegionChip = styled.button`
  padding: 7px 14px; border-radius: 999px; cursor: pointer; font-size: 12.5px; font-weight: 700;
  border: 1px solid ${p => (p.$on ? 'rgba(27,140,60,0.5)' : '#e2e8f0')};
  background: ${p => (p.$on ? 'rgba(27,140,60,0.12)' : '#fff')};
  color: ${p => (p.$on ? '#006200' : '#64748b')}; transition: all .15s;
  &:hover { border-color: rgba(27,140,60,0.45); }
`;

// Collect the region values present across the region charts (for the global filter options).
function regionsFromCharts(charts, cfg) {
  const labels = (cfg && cfg.category_labels) || {};
  const set = new Set();
  Object.values(charts || {}).forEach(c => {
    const f = c?.encoding?.x?.field || c?.encoding?.y?.field;
    if (f === 'region' || (c?.data || []).some(r => labels[r?.[f]])) {
      (c?.data || []).forEach(r => { if (r?.[f] != null) set.add(r[f]); });
    }
  });
  return [...set];
}

// ── Main Dashboard ────────────────────────────────────────────────────────────

// Generic styling pools (cycled) so KPIs and chart sections need no per-item code.
const GRADIENTS = [
  'linear-gradient(135deg,#1B8C3C 0%,#006200 100%)',
  'linear-gradient(135deg,#2b6cb0 0%,#1a4a8a 100%)',
  'linear-gradient(135deg,#e53e3e 0%,#9b2c2c 100%)',
  'linear-gradient(135deg,#38a169 0%,#276749 100%)',
  'linear-gradient(135deg,#6b46c1 0%,#44337a 100%)',
  'linear-gradient(135deg,#d53f8c 0%,#97266d 100%)',
];
const KPI_ICONS = { activity: MessageSquare, users: Users, 'trending-up': Zap, 'credit-card': Radio, database: Database };
const SECTION_ICONS = [MessageSquare, Zap, Activity, Radio, Database];

export default function Dashboard({ setActiveTab }) {
  const { charts, dashboardData, summaryKpis, loading, error } = useDashboard();
  const chat = useChat();
  // Dashboard → chat drill: clicking a bar opens a CHOOSER menu (not a sudden question); picking
  // an option sends a clean Arabic question into a new chat conversation.
  const [drillMenu, setDrillMenu] = useState(null);
  // Settings drawer (auto-refresh interval, full-refresh schedule — all wired to the orch scheduler).
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  // Multi-select global region filter: a Set of selected regions (empty = all). Toggle to add/remove.
  const [globalRegions, setGlobalRegions] = useState(() => new Set());
  const [filterCollapsed, setFilterCollapsed] = useState(false);
  // Drill-menu config comes from the pack (dashboards.json → drill), not hardcoded in this file.
  const drillCfg = dashboardData?.drill || {};
  const availableRegions = React.useMemo(() => regionsFromCharts(charts, drillCfg), [charts, drillCfg]);
  const toggleRegion = useCallback((r) => setGlobalRegions(prev => {
    const n = new Set(prev);
    n.has(r) ? n.delete(r) : n.add(r);
    return n;
  }), []);
  const handleOpenDrill = useCallback((payload) => setDrillMenu(payload), []);
  const handleDrill = useCallback((question) => {
    setDrillMenu(null);
    if (!question) return;
    if (setActiveTab) setActiveTab('chat');
    if (chat && chat.actions && chat.actions.sendMessage) chat.actions.sendMessage(question);
  }, [setActiveTab, chat]);
  // Data-challenge panels (Grain Problem + Office Mismatch) hidden for now.
  // Set this to true to bring them back below the charts.
  const SHOW_DATA_CHALLENGE_PANELS = false;
  const [grainExpanded, setGrainExpanded] = useState(false);

  if (loading) return (
    <Container><LoadingBox>Loading dashboard…</LoadingBox></Container>
  );
  if (error) return (
    <Container><LoadingBox style={{ color: '#e53e3e' }}>Failed to load: {error}</LoadingBox></Container>
  );

  // Header + optional insight panels all come from the backend payload (the pack),
  // so this component is domain-agnostic.
  const grain    = dashboardData?.grainComparison;
  const meta     = dashboardData?.meta || {};

  return (
    <Container>
      {/* Settings drawer (auto-refresh interval + full-refresh schedule) */}
      <DashboardSettings isOpen={isSettingsOpen} onClose={() => setIsSettingsOpen(false)} />

      <PageHeader>
        <div>
          <PageTitle>
            <Database color="#1B8C3C" size={24} />
            {meta.title || 'Operational Dashboard'}
          </PageTitle>
          <PageSubtitle>
            {meta.subtitle || ''}
          </PageSubtitle>
        </div>
        <HeaderControls>
          <IconButton onClick={() => setIsSettingsOpen(true)} title="الإعدادات / Settings"><Settings size={18} /></IconButton>
          <ExportButton />
        </HeaderControls>
      </PageHeader>

      {/* KPI Cards — generic, from the backend's summary_kpis (config-driven) */}
      <KpiCardsGrid>
        {(summaryKpis || []).map((k, i) => {
          const Icon = KPI_ICONS[k.icon] || Database;
          return (
            <KpiCard key={k.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.07 }}>
              <KpiIconWrapper $gradient={GRADIENTS[i % GRADIENTS.length]}><Icon /></KpiIconWrapper>
              <KpiContent>
                <KpiValue>{k.formatted ?? '—'}</KpiValue>
                <KpiLabel>{k.label_en || k.label}</KpiLabel>
              </KpiContent>
            </KpiCard>
          );
        })}
      </KpiCardsGrid>

      {/* Global region filter — collapsible; multi-select (toggle); re-scopes by-region charts at once */}
      {availableRegions.length > 1 && (
        <GlobalBar>
          <button className="gb-toggle" onClick={() => setFilterCollapsed(c => !c)}>
            <Filter /> فلترة حسب المنطقة
            {globalRegions.size > 0 && <span className="gb-count">{globalRegions.size}</span>}
            {filterCollapsed ? <ChevronDown /> : <ChevronUp />}
          </button>
          {!filterCollapsed && (
            <div className="gb-chips">
              <RegionChip $on={globalRegions.size === 0} onClick={() => setGlobalRegions(new Set())}>الكل</RegionChip>
              {availableRegions.map((r, i) => (
                <RegionChip key={i} $on={globalRegions.has(r)} onClick={() => toggleRegion(r)}>
                  {categoryAr(drillCfg, r)}
                </RegionChip>
              ))}
            </div>
          )}
        </GlobalBar>
      )}

      {/* Grain Problem Panel */}
      {SHOW_DATA_CHALLENGE_PANELS && grain && (
        <ProblemPanel $color="#e53e3e" $shadow="rgba(229,62,62,0.10)">
          <PanelTitle $color="#c53030" onClick={() => setGrainExpanded(p => !p)}>
            <AlertTriangle size={17} />
            THE GRAIN PROBLEM — "How many new connection requests?"
            <button>{grainExpanded ? <ChevronUp size={15}/> : <ChevronDown size={15}/>}</button>
          </PanelTitle>
          <PanelSubtitle>
            Both tables are in the connections domain. Without documented grain, a Text2SQL model picks the wrong table and returns a count {grain.error_factor}× too high — silently, with no error.
          </PanelSubtitle>

          {grainExpanded && (
            <GrainCols>
              <GrainBox>
                <GrainTag><XCircle size={10}/> WRONG TABLE</GrainTag>
                <GrainNumber>{grain.wrong_count?.toLocaleString()}</GrainNumber>
                <GrainDesc>
                  {`SELECT COUNT(*) FROM ${grain.wrong_table};\n→ ${grain.wrong_grain}`}
                </GrainDesc>
              </GrainBox>
              <GrainDivider>
                <FactorBadge>×{grain.error_factor}</FactorBadge>
                <FactorLabel>Error<br/>Factor</FactorLabel>
              </GrainDivider>
              <GrainBox $correct>
                <GrainTag $correct><CheckCircle size={10}/> CORRECT TABLE</GrainTag>
                <GrainNumber $correct>{grain.correct_count?.toLocaleString()}</GrainNumber>
                <GrainDesc>
                  {`SELECT COUNT(*) FROM ${grain.correct_table};\n→ ${grain.correct_grain}`}
                </GrainDesc>
              </GrainBox>
            </GrainCols>
          )}
        </ProblemPanel>
      )}

      {/* (Office-mismatch demo panel removed 2026-07-09 — the conformed dims cured the
          mismatch it demonstrated; it could only ever show two identical lists.) */}

      {/* Charts */}
      {charts && (
        <>
          {(() => {
            // Group the backend charts by their `group` and render generically.
            const groups = {};
            Object.entries(charts).forEach(([id, c]) => {
              if (!c || !c.data) return;
              const key = (c.group && c.group.en) || 'Charts';
              (groups[key] = groups[key] || []).push([id, c]);
            });
            return Object.entries(groups).map(([groupName, items], gi) => (
              <Section
                key={groupName}
                icon={SECTION_ICONS[gi % SECTION_ICONS.length]}
                title={groupName}
                gradient={GRADIENTS[gi % GRADIENTS.length]}
                cols={2}
              >
                {items.map(([id, c]) => (
                  <ChartPanel key={id} chartData={c} mv={c.source_mv} onOpenDrill={handleOpenDrill} globalRegions={globalRegions} cfg={drillCfg} />
                ))}
              </Section>
            ));
          })()}
        </>
      )}

      {/* Pinned charts — the user's saved charts, at the END of the dashboard (self-hides when none) */}
      <MyChartsSection />

      <DashboardDrillMenu drill={drillMenu} cfg={drillCfg} onPick={handleDrill} onClose={() => setDrillMenu(null)} />
    </Container>
  );
}
